<script setup lang="ts">
import BaseModal from '../BaseModal.vue'

const props = withDefaults(
	defineProps<{
		modelValue: boolean
		testMode: boolean
		errorCount?: number
	}>(),
	{
		testMode: false,
		errorCount: 0
	}
)

const emit = defineEmits(['update:modelValue', 'confirm-clean'])

const closeModal = () => {
	emit('update:modelValue', false)
}

const confirmClean = () => {
	emit('confirm-clean')
	closeModal()
}
</script>

<template>
	<BaseModal
		:model-value="props.modelValue"
		@update:model-value="$emit('update:modelValue', $event)"
		title="Limpiar Errores de Carga"
		:test-mode="props.testMode"
		custom-header-class="clean-error-modal-header"
		size="default"
		message="">
		<template #body>
			<div class="clean-error-content">
				<!-- Icono animado de advertencia -->
				<div class="warning-icon-container">
					<div class="warning-icon animate-pulse">
						<svg class="warning-svg" width="120" height="120" viewBox="0 0 100 100" fill="none">
							<circle cx="50" cy="50" r="48" fill="url(#warningGradient)" />
							<path d="M50 20 L82 78 H18 Z" stroke="#856404" stroke-width="4" fill="none" />
							<circle cx="50" cy="68" r="4" fill="#856404" />
							<rect x="46" y="40" width="8" height="18" rx="4" fill="#856404" />
							<defs>
								<linearGradient id="warningGradient" x1="0%" y1="0%" x2="100%" y2="100%">
									<stop offset="0%" style="stop-color: #fff3cd; stop-opacity: 1" />
									<stop offset="100%" style="stop-color: #ffeaa7; stop-opacity: 1" />
								</linearGradient>
							</defs>
						</svg>
					</div>
				</div>

				<!-- Contenido principal -->
				<div class="modal-content-section">
					<h3 class="modal-title">¿Desea eliminar la carga anterior?</h3>

					<!-- Alerta de errores -->
					<div class="error-alert">
						<div class="alert-content">
							<div class="alert-icon">
								<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
									<path
										fill-rule="evenodd"
										d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
										clip-rule="evenodd" />
								</svg>
							</div>
							<div class="alert-text">
								<p class="alert-title">Se encontraron {{ props.errorCount }} error(es) en la carga anterior</p>
								<p class="alert-description">Esta acción eliminará todos los errores y permitirá una nueva carga</p>
							</div>
						</div>
					</div>

					<!-- Información adicional -->
					<div class="info-alert">
						<div class="alert-content">
							<div class="alert-icon">
								<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
									<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
								</svg>
							</div>
							<div class="alert-text">
								<p class="alert-title">¿Qué sucede al confirmar?</p>
								<ul class="info-list">
									<li>Se eliminarán todos los errores de la carga anterior</li>
									<li>Podrá realizar una nueva carga de certificados</li>
									<li>Los certificados válidos se mantendrán intactos</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>

		<template #footer>
			<div class="modal-footer-actions">
				<button class="btn btn-secondary" @click="closeModal">
					<svg class="btn-icon" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
							clip-rule="evenodd" />
					</svg>
					Cancelar
				</button>
				<button class="btn btn-warning" @click="confirmClean">
					<svg class="btn-icon" fill="currentColor" viewBox="0 0 20 20">
						<path
							fill-rule="evenodd"
							d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
							clip-rule="evenodd" />
					</svg>
					Eliminar Errores
				</button>
			</div>
		</template>
	</BaseModal>
</template>

<style scoped>
/* Contenedor principal */
.clean-error-content {
	text-align: center;
	padding: 1.5rem;
}

/* Icono de advertencia */
.warning-icon-container {
	margin-bottom: 2rem;
}

.warning-icon {
	display: inline-block;
	animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.warning-svg {
	filter: drop-shadow(0 4px 8px rgba(255, 193, 7, 0.3));
}

@keyframes pulse {
	0%,
	100% {
		opacity: 1;
		transform: scale(1);
	}
	50% {
		opacity: 0.8;
		transform: scale(1.05);
	}
}

/* Sección de contenido */
.modal-content-section {
	max-width: 500px;
	margin: 0 auto;
}

.modal-title {
	font-size: 1.5rem;
	font-weight: 700;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	line-height: 1.3;
}

/* Alertas */
.error-alert,
.info-alert {
	border-radius: 12px;
	padding: 1.25rem;
	margin-bottom: 1rem;
	transition: all 0.3s ease;
	animation: fadeInUp 0.5s ease-out;
}

.error-alert {
	background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
	border: 2px solid #fcd34d;
	box-shadow: 0 4px 12px rgba(255, 193, 7, 0.15);
}

.info-alert {
	background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
	border: 2px solid #60a5fa;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.error-alert:hover,
.info-alert:hover {
	transform: translateY(-2px);
	box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.alert-content {
	display: flex;
	align-items: flex-start;
	gap: 1rem;
	text-align: left;
}

.alert-icon {
	flex-shrink: 0;
	width: 2rem;
	height: 2rem;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
}

.error-alert .alert-icon {
	background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
	color: #92400e;
}

.info-alert .alert-icon {
	background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
	color: white;
}

.alert-text {
	flex: 1;
}

.alert-title {
	font-size: 1rem;
	font-weight: 600;
	margin: 0 0 0.5rem 0;
	line-height: 1.4;
}

.error-alert .alert-title {
	color: #92400e;
}

.info-alert .alert-title {
	color: #1e40af;
}

.alert-description {
	font-size: 0.875rem;
	color: #92400e;
	margin: 0;
	line-height: 1.5;
}

/* Lista de información */
.info-list {
	list-style: none;
	padding: 0;
	margin: 0.5rem 0 0 0;
}

.info-list li {
	font-size: 0.875rem;
	color: #1e40af;
	margin-bottom: 0.25rem;
	line-height: 1.5;
	position: relative;
	padding-left: 1.25rem;
}

.info-list li:before {
	content: '•';
	position: absolute;
	left: 0;
	color: #3b82f6;
	font-weight: bold;
	font-size: 1.2rem;
}

.info-list li:last-child {
	margin-bottom: 0;
}

/* Footer con acciones */
.modal-footer-actions {
	display: flex;
	gap: 1rem;
	width: 100%;
}

/* Botones */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	padding: 0.75rem 1.5rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.3s ease;
	border: none;
	cursor: pointer;
	text-decoration: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	flex: 1;
	min-height: 44px;
}

.btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn:active {
	transform: translateY(0);
}

.btn-icon {
	width: 1rem;
	height: 1rem;
}

.btn-secondary {
	background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
	color: white;
	border: 2px solid #6c757d;
}

.btn-secondary:hover {
	background: linear-gradient(135deg, #495057 0%, #343a40 100%);
	border-color: #495057;
}

.btn-warning {
	background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
	color: #212529;
	border: 2px solid #ffc107;
}

.btn-warning:hover {
	background: linear-gradient(135deg, #e0a800 0%, #d39e00 100%);
	border-color: #e0a800;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Diseño responsivo */
@media (max-width: 768px) {
	.clean-error-content {
		padding: 1rem;
	}

	.modal-title {
		font-size: 1.25rem;
		margin-bottom: 1rem;
	}

	.warning-svg {
		width: 80px;
		height: 80px;
	}

	.error-alert,
	.info-alert {
		padding: 1rem;
		margin-bottom: 0.75rem;
	}

	.alert-content {
		gap: 0.75rem;
	}

	.alert-icon {
		width: 1.5rem;
		height: 1.5rem;
	}

	.modal-footer-actions {
		flex-direction: column;
		gap: 0.75rem;
	}

	.btn {
		width: 100%;
		padding: 0.875rem 1.25rem;
		font-size: 0.9rem;
	}
}

@media (max-width: 480px) {
	.clean-error-content {
		padding: 0.75rem;
	}

	.modal-title {
		font-size: 1.125rem;
	}

	.warning-svg {
		width: 60px;
		height: 60px;
	}

	.alert-content {
		flex-direction: column;
		text-align: center;
		gap: 0.5rem;
	}

	.alert-text {
		text-align: center;
	}

	.info-list li {
		text-align: center;
		padding-left: 0;
	}

	.info-list li:before {
		display: none;
	}
}

/* Utilidades */
.w-6 {
	width: 1.5rem;
}

.h-6 {
	height: 1.5rem;
}
</style>
